import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import PortfolioCollectionTile from './PortfolioCollectionTile';
import { PORTFOLIO_PAGE_CONTENT_FALLBACK } from '@/utils/gallery-api';

export default function FeaturedPortfolioCollections({
  collectionTiles,
  title = PORTFOLIO_PAGE_CONTENT_FALLBACK.title,
  intro = PORTFOLIO_PAGE_CONTENT_FALLBACK.intro,
}) {
  const [activeTileId, setActiveTileId] = useState(null);
  const sectionRef = useRef(null);

  const deactivateTiles = useCallback(() => {
    setActiveTileId(null);
  }, []);

  useEffect(() => {
    if (!activeTileId) return undefined;

    const handlePointerDown = event => {
      if (sectionRef.current?.contains(event.target)) return;
      deactivateTiles();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [activeTileId, deactivateTiles]);

  return (
    <Box
      ref={sectionRef}
      component="section"
      aria-labelledby="portfolio-collections-heading"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        flex: '0 0 auto',
        // Interior nav: 52px logo + spacing(2) py + 1px border (md+).
        minHeight: { md: 'calc(100dvh - 85px)' },
        py: { xs: 6, md: 8, xl: 10 },
        bgcolor: 'background.default',
        color: '#0a0a0a',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          width: '100%',
          px: { xs: 2, sm: 3, md: 6 },
          flex: '0 0 auto',
          mb: { xs: 4, md: 5 },
        }}
      >
        <Typography
          id="portfolio-collections-heading"
          component="h1"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            fontWeight: 500,
            fontSize: { xs: 22, md: 28, xl: 32 },
            lineHeight: 1.2,
            m: 0,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 48,
            height: 3,
            bgcolor: '#0a0a0a',
            mx: 'auto',
            mt: 1.25,
          }}
        />
        <Typography
          variant="body1"
          sx={{
            mt: { xs: 2.5, md: 2.5 },
            mx: 'auto',
            maxWidth: 900,
            lineHeight: 1.75,
          }}
        >
          {intro}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          width: '100%',
          flex: '0 0 auto',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(2, 1fr)',
            xl: 'repeat(4, 1fr)',
          },
        }}
      >
        {collectionTiles.map(collection => (
          <PortfolioCollectionTile
            key={collection.id}
            collection={collection}
            isActive={activeTileId === collection.id}
            onActivate={() => setActiveTileId(collection.id)}
            onDeactivate={deactivateTiles}
          />
        ))}
      </Box>
    </Box>
  );
}

FeaturedPortfolioCollections.propTypes = {
  title: PropTypes.string,
  intro: PropTypes.string,
  collectionTiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      coverImage: PropTypes.string,
      hoverSlides: PropTypes.array,
    }),
  ).isRequired,
};
